import React, { FC, useEffect } from "react";
import { Alert, Container } from "../../../components";
import { useQueryParam } from "../../../components/hooks";
import { connect, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { CART_ITEM_TYPE, FORM_ID, GTM_ECOMMERCE_STEP, PAYMENT_METHOD, PaymentMethod } from "../../../../../constants";
import PAGES from "../../../../../constants/pages";
import { DeliveryMethod, Form, ICartItemWithBundleId, RootState, StepIndicatorType } from "../../../../../models";
import { setNegativeFlash } from "../../../app/state/generalActions";
import { trackCheckoutAndNavigate } from "../../../google-analytics";
import { selectors as LOADING_SELECTORS } from "../../../loading";
import { shouldShowMemberStepWhenReSwitch as shouldShowMemberStepWhenReSwitchSelector } from "../../../navigation/state/selectors";
import { RegisterVoucher } from "../../../voucher";
import ContactCard from "../../components/molecules/ContactCard/ContactCard";
import LeaseLimitModal from "../../components/molecules/LeaseLimitModal/LeaseLimitModal";
import CheckoutPage from "../../components/organisms/CheckoutPage/CheckoutPage";
import DeliveryCard from "../../components/organisms/DeliveryCard/DeliveryCard";
import PaymentCard from "../../components/organisms/PaymentCard/PaymentCard";
import RegisteredMemberCardList from "../../components/organisms/RegisteredMemberCardList/RegisteredMemberCardList";
import { usePrevious } from "../../hooks";
import { debouncedFetchDeliveryAndPaymentMethods } from "../../state/actions/combinedActions";
import { hasExternalLegalOwner } from "../../state/selectors/accountSelectors";
import { getAllCartSkus, getAllItemsOfType } from "../../state/selectors/cartSelectors";
import * as COMBINED_SELECTORS from "../../state/selectors/combinedSelectors";
import * as CUSTOMER_INFO_SELECTORS from "../../state/selectors/customerInfoSelectors";
import { getDeliveryInfoMethods } from "../../state/selectors/deliveryInfoSelectors";
import { getFormById } from "../../state/selectors/formSelectors";
import getStepIndicatorData from "../../state/selectors/getStepIndicatorData";
import {
    getLeaseErrorCartItem,
    getPaymentMethods,
    getPaymentSelectedType,
    getPaymentSuccessRedirectUrl,
    isLeaseError as isLeaseErrorSelector,
} from "../../state/selectors/paymentSelectors";
import {
    getLegalSubscriptionId,
    hasSubscriptions as hasSubscriptionsSelector,
    isSubscriptionBroadband,
    isSubscriptionKeepExistingReSwitching,
} from "../../state/selectors/subscriptionsSelectors";
import TEXT from "../../text-consts";
import "./Summary.less";

interface StateProps {
    cartItem?: ICartItemWithBundleId;
    deliveryMethods: DeliveryMethod[];
    editDeliveryAddressForm: Form;
    hasBroadbandSubscriptions: boolean;
    hasExternalLegal: boolean;
    hasLegalOwnerEmail: boolean;
    hasReachedMaximumNumberOfSubscriptions: boolean;
    hasSubscriptions: boolean;
    isFetchingData: boolean;
    isLeaseError: boolean;
    isMBB: boolean;
    isMultipleSubscriptions: boolean;
    isOnlySubscriptionsToExistingCustomers: boolean;
    isReswitchAndHasNoMemberStep: boolean;
    isVoucherInCart: boolean;
    legalOwnerAddress: string;
    legalOwnerCity: string;
    legalOwnerEmail: string;
    legalOwnerFullName: string;
    legalOwnerPhone: string;
    paymentMethods: PaymentMethod[];
    paymentSelectedType?: PaymentMethod;
    paymentSuccessRedirectUrl: string;
    skus: string[];
    stepIndicator: StepIndicatorType;
    totalPaymentPrice: number;
}

const Summary: FC<StateProps> = ({
    cartItem,
    legalOwnerFullName,
    legalOwnerCity,
    legalOwnerEmail,
    legalOwnerPhone,
    legalOwnerAddress,
    hasLegalOwnerEmail,
    hasExternalLegal,
    hasBroadbandSubscriptions,
    hasSubscriptions,
    skus,
    isOnlySubscriptionsToExistingCustomers,
    paymentSuccessRedirectUrl,
    isFetchingData,
    editDeliveryAddressForm,
    isVoucherInCart,
    isMBB,
    totalPaymentPrice,
    deliveryMethods,
    paymentMethods,
    paymentSelectedType,
    isMultipleSubscriptions,
    stepIndicator,
    isReswitchAndHasNoMemberStep,
    hasReachedMaximumNumberOfSubscriptions,
    isLeaseError,
}) => {
    const contactInfoHiddenStyle = hasLegalOwnerEmail
        ? "summary-container__hidden"
        : "summary-container__section summary-container__to";
    const stringifySkus = JSON.stringify(skus);
    const isPaymentFailed = useQueryParam("paymentFailed");

    const location = useLocation();
    const dispatch = useDispatch();

    useEffect(() => {
        // Will do several things:
        // 1) Method will retrieve all delivery methods, pick up points and shops, and also update order with a pre-selected delivery method if none exists.
        //    We want this to happen everytime either the price or the hardware skus in cart has changed.
        // 2) Retrieve payment methods and pre-selected payment method if none exists (or if one not part of new methods exists)
        dispatch(debouncedFetchDeliveryAndPaymentMethods());
    }, [debouncedFetchDeliveryAndPaymentMethods, totalPaymentPrice, stringifySkus]);

    useEffect(() => {
        // Check if paymentFailed (kept persistent since debouncedFetchDeliveryAndPaymentMethods otherwise clears it on mount)
        if (isPaymentFailed) {
            dispatch(setNegativeFlash(TEXT.ERROR.PAYMENT, undefined, true));
        }
    }, [isPaymentFailed, setNegativeFlash]);

    const prevPaymentSuccessRedirectUrl = usePrevious(paymentSuccessRedirectUrl);
    useEffect(() => {
        if (paymentSuccessRedirectUrl && paymentSuccessRedirectUrl !== prevPaymentSuccessRedirectUrl) {
            dispatch(trackCheckoutAndNavigate(GTM_ECOMMERCE_STEP.SUMMARY, paymentSuccessRedirectUrl));
        }
    }, [paymentSuccessRedirectUrl, prevPaymentSuccessRedirectUrl, trackCheckoutAndNavigate]);

    let pageIndex = 2;
    if (hasExternalLegal) {
        pageIndex = 4;
    } else if (hasSubscriptions && !hasBroadbandSubscriptions) {
        pageIndex = 3;
    }

    return (
        <CheckoutPage
            isReadyToContinue={true}
            pageHeading={
                isOnlySubscriptionsToExistingCustomers ? TEXT.SUMMARY.HEADING_NO_DELIVERY : TEXT.SUMMARY.HEADING
            }
            pageIndex={pageIndex}
            hasSubscription={hasSubscriptions}
            isReswitchAndHasNoMemberStep={isReswitchAndHasNoMemberStep}
            isMBB={isMBB}
            isFetchingData={isFetchingData}
            stepIndicator={stepIndicator}
            path={location.pathname}
        >
            {isLeaseError && cartItem && (
                <LeaseLimitModal
                    cartItem={cartItem}
                    open={isLeaseError}
                    maxAllowedLeaseAmount={0}
                    close={async () => {
                        dispatch({ type: "CHECKOUT_RESET_LEASE_ERROR" });
                    }}
                    urlForTracking={PAGES.CHECKOUT.SUMMARY.URL}
                />
            )}
            {hasReachedMaximumNumberOfSubscriptions && (
                <Container className="beta-checkout--center spacer-top" size="small" noMargin noPadding>
                    <Alert title={TEXT.SUMMARY.MAX_NUMBER_OF_SUBSCRIPTIONS.HEADING} kind="neutral" icon="alert">
                        {TEXT.SUMMARY.MAX_NUMBER_OF_SUBSCRIPTIONS.DESCRIPTION}
                    </Alert>
                </Container>
            )}
            {isMultipleSubscriptions && (
                <RegisteredMemberCardList showList={true} noMargin={hasReachedMaximumNumberOfSubscriptions} />
            )}
            <ContactCard
                headingText={`${TEXT.SUMMARY.RECEIVER}:`}
                name={legalOwnerFullName}
                address={legalOwnerAddress}
                city={legalOwnerCity}
                deliveryAddressForm={editDeliveryAddressForm}
                contactPhone={legalOwnerPhone}
                contactEmail={legalOwnerEmail}
                showContactInfo={true}
                isSummary={true}
            />
            {isOnlySubscriptionsToExistingCustomers && (
                <Container className={contactInfoHiddenStyle} size="small" whiteBg noPaddingTop noMargin>
                    <div className="summary-container__to__block">
                        <Alert className="subscription-age-not-ok" title="Obs!" kind="neutral" icon="alert">
                            {TEXT.PERSONAL_DETAILS.LEGAL_OWNER.MISSING_EMAIL_WARNING}
                        </Alert>
                    </div>
                </Container>
            )}
            {deliveryMethods.length > 0 && <DeliveryCard />}
            {((paymentMethods.length > 0 && totalPaymentPrice > 0) ||
                paymentSelectedType === PAYMENT_METHOD.INVOICE ||
                isVoucherInCart) && <PaymentCard />}
            <RegisterVoucher />
        </CheckoutPage>
    );
};

const mapStateToProps = (state: RootState): StateProps => ({
    cartItem: getLeaseErrorCartItem(state),
    isFetchingData: LOADING_SELECTORS.isLoading(state),
    skus: getAllCartSkus(state),
    totalPaymentPrice: COMBINED_SELECTORS.getTotalPaymentPrice(state),
    isVoucherInCart: getAllItemsOfType(state, CART_ITEM_TYPE.VOUCHER).length > 0,
    isReswitchAndHasNoMemberStep:
        isSubscriptionKeepExistingReSwitching(state) && !shouldShowMemberStepWhenReSwitchSelector(state),
    isMBB: isSubscriptionBroadband(state, getLegalSubscriptionId(state)),
    legalOwnerFullName: CUSTOMER_INFO_SELECTORS.getLegalOwnerFullName(state),
    legalOwnerCity: CUSTOMER_INFO_SELECTORS.getLegalOwnerFullCity(state),
    editDeliveryAddressForm: getFormById(state, FORM_ID.EDIT_DELIVERY_ADDRESS),
    legalOwnerAddress: CUSTOMER_INFO_SELECTORS.getLegalOwnerAddress(state),
    legalOwnerPhone: CUSTOMER_INFO_SELECTORS.getLegalOwnerPhone(state),
    legalOwnerEmail: CUSTOMER_INFO_SELECTORS.getLegalOwnerEmail(state),
    hasLegalOwnerEmail: CUSTOMER_INFO_SELECTORS.hasLegalOwnerEmail(state),
    paymentSuccessRedirectUrl: getPaymentSuccessRedirectUrl(state),
    deliveryMethods: getDeliveryInfoMethods(state),
    paymentMethods: getPaymentMethods(state),
    paymentSelectedType: getPaymentSelectedType(state),
    stepIndicator: getStepIndicatorData(state),
    hasExternalLegal: hasExternalLegalOwner(state),
    hasReachedMaximumNumberOfSubscriptions: COMBINED_SELECTORS.hasReachedMaximumNumberOfSubscriptions(state),
    isLeaseError: isLeaseErrorSelector(state),
    // MultiSub
    hasBroadbandSubscriptions: COMBINED_SELECTORS.hasBroadbandInCart(state),
    hasSubscriptions: hasSubscriptionsSelector(state),
    isMultipleSubscriptions: COMBINED_SELECTORS.isMultipleSubscriptions(state),
    isOnlySubscriptionsToExistingCustomers: COMBINED_SELECTORS.isOnlySubscriptionsToExistingCustomers(state),
});

export default connect(mapStateToProps)(Summary);
